import { action, observable } from 'mobx'
import { debounceTime, tap } from 'rxjs'
import { validationContext } from 'validx'
import { type ScopeChangeRequestItem } from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { mustBeInRange } from '@/utils/validx-util'

export class CostPerCustomServiceViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private _hasPendingUpdate = false

  @observable accessor fromItem: ScopeChangeRequestItem
  @observable accessor newPrice: number | null = null
  @observable accessor newProPrice: number | null = null

  readonly validation = validationContext(this, {
    newPrice: [mustBeInRange(0, Infinity, `Can't be negative`)],
    newProPrice: [mustBeInRange(0, Infinity, `Can't be negative`)],
  })

  constructor(
    item: ScopeChangeRequestItem,
    onPriceUpdate: () => Promise<void>,
  ) {
    this.fromItem = item
    this.newPrice = item.price
    this.newProPrice = item.proPrice

    this._lifecycle.manage(
      toStream(() => [this.newPrice, this.newProPrice]).pipe(
        tap(() => (this._hasPendingUpdate = true)),
        debounceTime(500),
        tap(() => {
          this._hasPendingUpdate = false
          void onPriceUpdate()
        }),
      ),
    )
  }

  /**
   * Resets the prices to the item's, without reporting it as a price update.
   */
  @action.bound
  reset() {
    const active = this._lifecycle.active
    this.deactivate()
    this.newPrice = this.fromItem.price
    this.newProPrice = this.fromItem.proPrice
    if (active) {
      this.activate()
    }
  }

  /**
   * Drops the debounced price update, if one is pending.
   *
   * @returns Whether an update was pending.
   */
  @action.bound
  cancelPendingUpdate() {
    const hadPendingUpdate = this._hasPendingUpdate
    if (hadPendingUpdate) {
      this.deactivate()
      this.activate()
    }
    return hadPendingUpdate
  }

  validate() {
    return this.validation.reset().validate().isValid
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._hasPendingUpdate = false
    this._lifecycle.deactivate()
  }
}
